import {
  logisticsSnapshotsEqual,
  OrdersApiAdapter,
  type LogisticsSnapshot,
  type RefreshOrderLogisticsResponse,
} from '@modules/Orders/List/adapters/OrdersApiAdapter';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { apiClientWH } from '@shared/services';
import { formatDateTime } from '@shared/utils/FormatUtils';
import { AlertCircle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface RefreshLogisticsResultModalRef {
  open: (payload: { orderId: number; onClose?: () => void }) => void;
  close: () => void;
}

function courierLabel(snapshot: LogisticsSnapshot | undefined): string {
  if (!snapshot?.shipment_status_mapped) return '—';
  const m = snapshot.shipment_status_mapped;
  if ('status_description' in m && m.status_description) return m.status_description;
  if ('status_code' in m && m.status_code) return m.status_code;
  return `#${m.id}`;
}

function internalLabel(snapshot: LogisticsSnapshot | undefined): string {
  return snapshot?.shipment_status?.name ?? '—';
}

/** Interpreta `YYYY-MM-DD HH:mm:ss` (Laravel) como fecha/hora local. */
function parseLaravelDateTimeString(s: string): Date | null {
  const t = s.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/.exec(t);
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      Number(m[6])
    );
  }
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d;
}

function formatTrackingEventDateTime(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const d = parseLaravelDateTimeString(value);
  if (!d) return '—';
  return formatDateTime(d);
}

type RefreshLogisticsResultModalProps = Record<never, never>;

type ViewState = 'loading' | 'result' | 'error';

export const RefreshLogisticsResultModal = forwardRef<
  RefreshLogisticsResultModalRef,
  RefreshLogisticsResultModalProps
>((_, ref) => {
  const { t } = useTranslation('ordersModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const apiRef = useRef(new OrdersApiAdapter(createHttpClient(apiClientWH)));

  const [view, setView] = useState<ViewState>('loading');
  const [data, setData] = useState<RefreshOrderLogisticsResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sessionRef = useRef(0);
  const onCloseCallbackRef = useRef<(() => void) | undefined>(undefined);
  const shouldRefetchOnCloseRef = useRef(false);

  const handleClosed = useCallback(() => {
    sessionRef.current += 1;
    if (shouldRefetchOnCloseRef.current) {
      onCloseCallbackRef.current?.();
    }
    shouldRefetchOnCloseRef.current = false;
    onCloseCallbackRef.current = undefined;
    setData(null);
    setErrorMessage(null);
    setView('loading');
  }, []);

  useImperativeHandle(ref, () => ({
    open: (payload) => {
      const session = ++sessionRef.current;
      onCloseCallbackRef.current = payload.onClose;
      shouldRefetchOnCloseRef.current = false;
      setView('loading');
      setData(null);
      setErrorMessage(null);
      modalRef.current?.openModal();

      void (async () => {
        try {
          const res = await apiRef.current.refreshOrderLogistics(payload.orderId);
          if (session !== sessionRef.current) return;
          if (!res.success) {
            setErrorMessage(res.message || t('toast.error.description'));
            setView('error');
            return;
          }
          setData(res);
          setView('result');
          shouldRefetchOnCloseRef.current = true;
        } catch (error: unknown) {
          if (session !== sessionRef.current) return;
          const apiMessage =
            (error as { message?: string })?.message ||
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            t('toast.error.description');
          setErrorMessage(apiMessage);
          setView('error');
        }
      })();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
  }));

  const handleClose = () => {
    modalRef.current?.closeModal();
  };

  const detail = data?.detail;
  const prev = detail?.previous_values;
  const next = detail?.updated_values;
  const noChange =
    view === 'result' && data && prev && next ? logisticsSnapshotsEqual(prev, next) : false;

  const modalTitle = useMemo(() => {
    if (view === 'loading') return t('modal.refreshLogisticsResult.titleLoading');
    if (view === 'error') return t('modal.refreshLogisticsResult.titleError');
    if (view === 'result' && noChange) return t('modal.refreshLogisticsResult.titleNoChange');
    if (view === 'result') return t('modal.refreshLogisticsResult.titleUpdated');
    return t('modal.refreshLogisticsResult.titleLoading');
  }, [view, noChange, t]);

  const modalType =
    view === 'loading'
      ? 'default'
      : view === 'error'
        ? 'error'
        : noChange
          ? 'info'
          : 'success';

  const lb = t('modal.refreshLogisticsResult.before');
  const la = t('modal.refreshLogisticsResult.after');

  const rows = [
    {
      label: t('modal.refreshLogisticsResult.courierDescription'),
      before: courierLabel(prev),
      after: courierLabel(next),
    },
    {
      label: t('modal.refreshLogisticsResult.logisticsStatus'),
      before: internalLabel(prev),
      after: internalLabel(next),
    },
    {
      label: t('modal.refreshLogisticsResult.lastEventDate'),
      before: formatTrackingEventDateTime(prev?.date_tracking_update ?? null),
      after: formatTrackingEventDateTime(next?.date_tracking_update ?? null),
    },
  ];

  return (
    <ModalCenter
      ref={modalRef}
      title={modalTitle}
      type={modalType}
      width="lg"
      onCloseModal={handleClosed}
      footer={
        <div className="flex w-full justify-end">
          <Button type="button" variant="primary" size="lg" onClick={handleClose}>
            {t('modal.refreshLogisticsResult.done')}
          </Button>
        </div>
      }
    >
      {view === 'loading' && (
        <div
          className="flex flex-col items-center justify-center gap-5 rounded-xl border border-border bg-muted/20 px-4 py-10 text-foreground transition-colors"
          data-testid="refresh-logistics-loading"
          role="status"
          aria-live="polite"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/60">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" aria-hidden />
          </div>
          <div className="max-w-sm space-y-2 text-center">
            <p className="text-sm font-medium text-foreground">{t('modal.refreshLogisticsResult.loading')}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('modal.refreshLogisticsResult.loadingSub')}</p>
          </div>
        </div>
      )}

      {view === 'error' && errorMessage && (
        <div className="flex flex-col items-center gap-4 py-2 text-center sm:py-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" aria-hidden />
          </div>
          <div className="max-w-md space-y-2">
            <p className="text-sm font-medium text-foreground">{errorMessage}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('modal.refreshLogisticsResult.errorHint')}</p>
          </div>
        </div>
      )}

      {view === 'result' && data && (
        <div className="space-y-5 text-foreground">
          {noChange ? (
            <div
              className="flex gap-3 rounded-lg border border-border bg-muted/30 p-4 transition-colors"
              data-testid="refresh-logistics-no-change-banner"
            >
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
              <p className="text-sm leading-relaxed text-foreground">{t('modal.refreshLogisticsResult.noChangeBanner')}</p>
            </div>
          ) : (
            <div
              className="flex gap-3 rounded-lg border border-border bg-primary/5 p-4 ring-1 ring-primary/10 transition-colors"
              data-testid="refresh-logistics-changed-banner"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
              <p className="text-sm leading-relaxed text-foreground">{t('modal.refreshLogisticsResult.changedBanner')}</p>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[280px] border-collapse text-sm" data-testid="refresh-logistics-compare-table">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="w-[36%] px-3 py-2.5 text-left font-medium text-muted-foreground" scope="col" />
                  <th className="px-3 py-2.5 text-left font-medium text-foreground" scope="col">
                    {lb}
                  </th>
                  <th className="px-3 py-2.5 text-left font-medium text-foreground" scope="col">
                    {la}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="border-b border-border last:border-b-0">
                    <th
                      scope="row"
                      className="px-3 py-2.5 text-left font-normal text-muted-foreground align-top"
                    >
                      {row.label}
                    </th>
                    <td className="px-3 py-2.5 text-foreground break-words align-top" data-testid="refresh-logistics-before">
                      {row.before}
                    </td>
                    <td className="px-3 py-2.5 text-foreground break-words align-top" data-testid="refresh-logistics-after">
                      {row.after}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ModalCenter>
  );
});

RefreshLogisticsResultModal.displayName = 'RefreshLogisticsResultModal';
